import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { DocumentType } from '@lib/models/generated/graphql';
import { MyStelliumService } from '@lib/services/mystellium.service';
import { ReferenceDataService } from '@lib/services/reference-data.service';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { OuiSelectOption, OuiValidationError } from 'omnium-ui/form-field';
import { combineLatest } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentRequestPanelService } from './document-request-panel.service';
import { EmailPreviewDialogComponent } from './email-preview-dialog/email-preview-dialog.component';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import {
  AccountStatus,
  CreateDocumentRequestsInput,
  DocumentEmailTemplateType,
  DocumentRequestOutput,
  DocumentStatus,
} from '@lib/models/generated/myStellium-graphql';
import { OuiTagStatus } from 'omnium-ui/tag';
import { isNotificationDeliveryError } from '@lib/utils/graphql-error.util';
import { maxLengthError, requiredError } from '@lib/utils/errors-messages';
import { MagicLinkDialogComponent } from './magic-link-dialog/magic-link-dialog.component';

// Longueur maximale de DocumentRequest.DocumentTypeName dans MyStellium
const MAX_LABEL_LENGTH = 100;
const MAX_REQUESTS_PER_DOCUMENT = 10;

const duplicateLabelError: OuiValidationError = {
  code: 'duplicateLabel',
  message: 'Ce libellé est déjà utilisé pour ce type de document',
};

interface ExistingRequestStatus {
  statuses: DocumentStatus[];
  label: string;
  tagStatus: OuiTagStatus;
  color: string;
  icon: string;
}

const EXISTING_REQUEST_STATUSES: ExistingRequestStatus[] = [
  { statuses: [DocumentStatus.ToReview], label: 'À contrôler', tagStatus: 'todo', color: 'color-alert', icon: 'docs' },
  { statuses: [DocumentStatus.Rejected], label: 'Refusé', tagStatus: 'error', color: 'color-error', icon: 'scan_delete' },
  {
    statuses: [DocumentStatus.Waiting],
    label: 'En attente',
    tagStatus: 'transition',
    color: 'color-transition',
    icon: 'hourglass_empty',
  },
];

interface DocumentRequestFormEntry {
  id: number;
  // Source de vérité du libellé (envoi et validité de la demande), synchronisée depuis le champ
  label: string;
  control: FormControl<string>;
}

interface DocumentRequestFormItem {
  key: string;
  label: string;
  categoryCode: string;
  documentTypeCodes: string[];
  selected: boolean;
  alreadyRequested: boolean;
  // Demandes à créer pour ce document : la première porte le libellé du type, les suivantes sont ses duplications
  entries: DocumentRequestFormEntry[];
}

interface SelectedDocumentRequest {
  item: DocumentRequestFormItem;
  entry: DocumentRequestFormEntry;
}

interface ExistingDocumentRequest {
  id: number;
  label: string;
  requestStatus: ExistingRequestStatus;
}

interface DocumentRequestFormModel {
  items: DocumentRequestFormItem[];
}

export interface DocumentRequestSelection {
  documentTypes: DocumentType[];
}

interface DocumentRequestCategory extends OuiSelectOption<string> {
  total: number;
  selected: number;
}

interface DocumentRequestSubmitResult {
  success: boolean;
  magicLinkPath: string | null;
}

@Component({
  selector: 'app-document-request-creation',
  imports: [SHARED_IMPORTS],
  templateUrl: './document-request-creation.component.html',
  styleUrl: './document-request-creation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentRequestCreationComponent {
  private readonly referenceDataService = inject(ReferenceDataService);
  private readonly myStelliumService = inject(MyStelliumService);
  private readonly investisseurService = inject(InvestisseurService);
  readonly documentRequestPanelService = inject(DocumentRequestPanelService);
  private readonly dialogService = inject(OuiDialogService);
  private readonly snackbarService = inject(OuiSnackbarService);

  private readonly documentTypes = signal<DocumentType[]>([]);
  private readonly categoryOptions = signal<OuiSelectOption<string>[]>([]);
  private readonly model = signal<DocumentRequestFormModel>({ items: [] });
  private nextEntryId = 0;
  private readonly derNotTransmittedMessage =
    "Les demandes de documents ont été créées, mais le DER n'a pas pu être transmis à l'investisseur.";

  readonly isBulk = computed(() => this.documentRequestPanelService.bulkRecipients() !== null);
  readonly recipients = computed(() =>
    (this.documentRequestPanelService.bulkRecipients() ?? []).map(recipient => ({
      ...recipient,
      initials: this.getInitials(recipient.name),
    }))
  );
  readonly recipientsExpanded = signal<boolean>(true);

  readonly accessWithMagicLink = computed(() =>
    this.isBulk()
      ? this.recipients().every(recipient => recipient.accessWithMagicLink)
      : this.myStelliumService.documentRequestData()?.accountStatus !== AccountStatus.EntraConnected
  );
  // En demande groupée, chaque investisseur est toujours notifié par email automatique
  readonly hasValidEmail = computed<boolean>(() => this.isBulk() || !!this.investisseurService.investisseurEmail());
  readonly notifyByEmailChoice = signal<boolean>(true);
  readonly notifyByEmail = computed<boolean>(() => this.isBulk() || (this.hasValidEmail() && this.notifyByEmailChoice()));
  readonly saving = signal<boolean>(false);

  readonly alreadyRequestedControl = new FormControl<boolean>({ value: true, disabled: true }, { nonNullable: true });
  readonly labelErrors = [requiredError, maxLengthError(MAX_LABEL_LENGTH), duplicateLabelError];
  readonly maxRequestsPerDocument = MAX_REQUESTS_PER_DOCUMENT;

  readonly search = signal<string>('');
  readonly selectedCategoryCode = signal<string | null>(null);

  readonly visibleItems = computed(() => {
    const category = this.selectedCategoryCode();
    const search = this.normalizeSearchText(this.search());

    return this.model().items.filter(
      item => (!category || item.categoryCode === category) && (!search || this.normalizeSearchText(item.label).includes(search))
    );
  });

  readonly selectedItems = computed(() => this.model().items.filter(item => item.selected));
  readonly selectedRequests = computed<SelectedDocumentRequest[]>(() =>
    this.selectedItems().flatMap(item => item.entries.map(entry => ({ item, entry })))
  );
  private readonly labelsValid = computed<boolean>(() =>
    this.selectedRequests().every(({ item, entry }) => !this.getLabelErrors(item, entry.id, entry.label))
  );
  readonly existingRequests = computed<ExistingDocumentRequest[]>(() => {
    const items = this.model().items;
    return this.getExistingRequests()
      .map(request => {
        const codes = this.getRequestTypeCodes(request);
        const item = items.find(item => item.documentTypeCodes.some(code => codes.includes(code)));
        const requestStatus = EXISTING_REQUEST_STATUSES.find(status => status.statuses.includes(request.status));
        return item && requestStatus ? { id: request.id, label: request.documentTypeName?.trim() || item.label, requestStatus } : null;
      })
      .filter((request): request is ExistingDocumentRequest => !!request)
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  });

  readonly categories = computed<DocumentRequestCategory[]>(() => {
    const items = this.model().items;
    return this.categoryOptions()
      .map(category => ({
        ...category,
        total: items.filter(item => item.categoryCode === category.value).length,
        selected: items.filter(item => item.categoryCode === category.value && item.selected && !item.alreadyRequested).length,
      }))
      .filter(category => category.total > 0)
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  readonly selectedCategory = computed<DocumentRequestCategory | undefined>(() =>
    this.categories().find(category => category.value === this.selectedCategoryCode())
  );

  readonly allDocumentsCount = computed<number>(() => this.model().items.length);
  readonly selectedCount = computed<number>(() => this.selectedItems().length);

  readonly submitLabel = computed<string>(() =>
    !this.notifyByEmail() && this.accessWithMagicLink() ? 'Valider et générer le lien' : 'Valider'
  );

  readonly submitDisabled = computed<boolean>(
    () =>
      !this.labelsValid() ||
      (this.isBulk()
        ? this.selectedCount() === 0 || this.recipients().length === 0
        : this.selectedCount() === 0 && !this.accessWithMagicLink())
  );

  constructor() {
    combineLatest([this.referenceDataService.getDocumentCategories(), this.referenceDataService.getFilteredDocumentTypes()])
      .pipe(takeUntilDestroyed())
      .subscribe(([categories, documentTypes]) => {
        this.categoryOptions.set(categories ?? []);
        this.documentTypes.set(documentTypes ?? []);
        this.rebuildForm();
      });

    effect(() => {
      this.myStelliumService.documentRequestData();
      this.isBulk();
      untracked(() => this.rebuildForm());
    });

    effect(() => {
      this.documentRequestPanelService.openingCount();
      untracked(() => this.resetForm());
    });
  }

  selectCategory(categoryCode: string | null | undefined): void {
    this.selectedCategoryCode.set(categoryCode ?? null);
  }

  private normalizeSearchText(value: string): string {
    return value
      .trim()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLocaleLowerCase();
  }

  private getInitials(name: string): string {
    const words = name.split(' ').filter(word => !!word);
    return `${words[0]?.[0] ?? ''}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase();
  }

  removeRequest(itemKey: string, entryId: number): void {
    this.updateItem(itemKey, item =>
      this.withEntries(
        item,
        item.entries.filter(entry => entry.id !== entryId)
      )
    );
    this.revalidateLabels(itemKey);
  }

  duplicateRequest(itemKey: string, entryId: number): void {
    this.updateItem(itemKey, item => {
      if (item.entries.length >= MAX_REQUESTS_PER_DOCUMENT) return item;
      const entries = [...item.entries];
      entries.splice(entries.findIndex(entry => entry.id === entryId) + 1, 0, this.createEntry(item.key, this.getDuplicateLabel(item)));
      return this.withEntries(item, entries);
    });
  }

  updateLabel(itemKey: string, entryId: number, label: string): void {
    this.updateItem(itemKey, item => ({
      ...item,
      entries: item.entries.map(entry => (entry.id === entryId ? { ...entry, label } : entry)),
    }));
    this.revalidateLabels(itemKey, entryId);
  }

  removeRecipient(recipientId: number): void {
    this.documentRequestPanelService.removeBulkRecipient(recipientId);
  }

  setSelected(itemKey: string, selected: boolean): void {
    this.updateItem(itemKey, item =>
      this.withEntries(item, !selected ? [] : item.entries.length > 0 ? item.entries : [this.createEntry(item.key, item.label)])
    );
  }

  private updateItem(itemKey: string, update: (item: DocumentRequestFormItem) => DocumentRequestFormItem): void {
    this.model.update(model => ({
      items: model.items.map(item => (item.key === itemKey && !item.alreadyRequested ? update(item) : item)),
    }));
  }

  private withEntries(item: DocumentRequestFormItem, entries: DocumentRequestFormEntry[]): DocumentRequestFormItem {
    return { ...item, entries, selected: entries.length > 0 };
  }

  private createEntry(itemKey: string, label: string): DocumentRequestFormEntry {
    const id = this.nextEntryId++;
    // Le champ valide sa saisie en cours face aux libellés des autres exemplaires du modèle
    const control = new FormControl<string>(label, {
      nonNullable: true,
      validators: control => {
        const item = this.model().items.find(item => item.key === itemKey);
        return item ? this.getLabelErrors(item, id, control.value) : null;
      },
    });
    return { id, label, control };
  }

  // Les duplications d'un document doivent porter des libellés différents pour être distinguées dans MyStellium
  private getLabelErrors(item: DocumentRequestFormItem, entryId: number, label: string): ValidationErrors | null {
    const normalizedLabel = this.normalizeSearchText(label);
    if (!normalizedLabel) return { required: true };
    if (label.trim().length > MAX_LABEL_LENGTH) return { maxlength: { requiredLength: MAX_LABEL_LENGTH } };
    return item.entries.some(entry => entry.id !== entryId && this.normalizeSearchText(entry.label) === normalizedLabel)
      ? { duplicateLabel: true }
      : null;
  }

  // L'unicité d'un libellé dépend des autres exemplaires du même document
  private revalidateLabels(itemKey: string, changedEntryId?: number): void {
    this.model()
      .items.find(item => item.key === itemKey)
      ?.entries.filter(entry => entry.id !== changedEntryId)
      .forEach(entry => entry.control.updateValueAndValidity({ emitEvent: false }));
  }

  private getDuplicateLabel(item: DocumentRequestFormItem): string {
    const labels = new Set(item.entries.map(entry => this.normalizeSearchText(entry.label)));
    for (let index = 2; ; index++) {
      const suffix = ` (${index})`;
      const label = `${item.label.slice(0, MAX_LABEL_LENGTH - suffix.length)}${suffix}`;
      if (!labels.has(this.normalizeSearchText(label))) return label;
    }
  }

  openEmailPreview(): void {
    this.dialogService.openDialog(
      EmailPreviewDialogComponent,
      {
        templateType: DocumentEmailTemplateType.DocumentRequestEmail,
        documents: this.selectedRequests().map(({ entry }) => ({ name: entry.label.trim() })),
        investorName: this.isBulk() ? "Nom de l'investisseur" : (this.investisseurService.investisseurNameSig() ?? ''),
        accessWithMagicLink: this.accessWithMagicLink(),
      },
      // Hauteur fixe pour que l'aperçu remplisse le dialog avec un seul scroll (celui de l'iframe)
      '90vh',
      '1000px'
    );
  }

  async submit(): Promise<void> {
    const isBulk = this.isBulk();
    this.saving.set(true);
    try {
      const result = isBulk ? await this.createBulkDocumentRequests() : await this.createDocumentRequests();
      if (!result.success) {
        return;
      }
      if (!isBulk) {
        await this.myStelliumService.getInvestisseurDocumentRequestsForConsultant(this.investisseurService.investisseurSig()?.id ?? 0);
      }
      this.documentRequestPanelService.closePanel();
      if (result.magicLinkPath) {
        this.dialogService.openDialog(MagicLinkDialogComponent, { magicLinkPath: result.magicLinkPath });
      }
    } finally {
      this.saving.set(false);
    }
  }

  private async createDocumentRequests(): Promise<DocumentRequestSubmitResult> {
    const investisseurId = this.investisseurService.investisseurSig()?.id ?? 0;
    const notifyByEmail = this.notifyByEmail();
    const requireDerValidation = this.documentRequestPanelService.derInvestisseurIds().includes(investisseurId);
    const input: CreateDocumentRequestsInput = {
      investisseurId,
      notifyInvestor: notifyByEmail,
      requireDerValidation,
      documentRequests: this.getDocumentRequests(),
    };
    try {
      // Create the document requests
      const result = await this.myStelliumService.createDocumentRequests(input);
      // Send the DER the investisseur has to validate
      const derTransmitted = await this.transmitDer(result?.derUploadUrl);
      if (derTransmitted) {
        this.snackbarService.open('Les demandes de documents ont été créées avec succès.', 'success');
      }
      const magicLinkPath = !notifyByEmail && this.accessWithMagicLink() ? `${result?.basePath}${result?.key}` : null;
      return { success: true, magicLinkPath };
    } catch (error) {
      console.error(error);
      // Handle notification delivery errors separately
      if (isNotificationDeliveryError(error)) {
        // The requests were created before the email failed, so the DER upload URL is retrieved by replaying them without notification
        const derTransmitted = requireDerValidation ? await this.retryDerTransmission(input) : true;
        if (derTransmitted) {
          this.snackbarService.open(
            "Les demandes de documents ont été créées, mais l'email de notification n'a pas pu être envoyé à l'investisseur.",
            'warning'
          );
        }
        return { success: true, magicLinkPath: null };
      }
      this.snackbarService.open('Une erreur est survenue lors de la création des demandes de documents.', 'error');
      return { success: false, magicLinkPath: null };
    }
  }

  private async createBulkDocumentRequests(): Promise<DocumentRequestSubmitResult> {
    const recipients = this.recipients();
    const investisseurIds = recipients.map(recipient => recipient.id);
    try {
      // Create the bulk document requests
      const result = await this.myStelliumService.createBulkDocumentRequests({
        investisseurIds,
        documentRequests: this.getDocumentRequests(),
        derInvestisseurIds: this.documentRequestPanelService.derInvestisseurIds().filter(id => investisseurIds.includes(id)),
      });

      // Send the DER the investisseurs have to validate
      const derTransmitted = await this.transmitDer(result.derUploadUrl);

      // Let the investisseurs table deselect the investisseurs whose request was sent
      const failedIds = new Set<number>(result.errors.map(error => error.investisseurId));
      const sentIds = investisseurIds.filter(id => !failedIds.has(id));
      if (sentIds.length > 0) {
        this.documentRequestPanelService.notifyBulkRequestsSent(sentIds);
      }

      // Notify the user and close the panel if there are no errors
      if (result.errors.length === 0) {
        if (derTransmitted) {
          this.snackbarService.open('Les demandes de documents ont été envoyées avec succès.', 'success');
        }
        return { success: true, magicLinkPath: null };
      }

      // Map the failed requests to the corresponding recipient names for better error messages
      const namesById = new Map(recipients.map(recipient => [recipient.id, recipient.name]));
      const failedNames = result.errors.map(error => namesById.get(error.investisseurId) || `#${error.investisseurId}`);
      this.snackbarService.open(`Échec de la demande de documents pour : ${failedNames.join(', ')}`, 'error');
      // If some requests failed but not all, close the panel to allow retry for the remaining ones
      return { success: result.errors.length < investisseurIds.length, magicLinkPath: null };
    } catch (error) {
      console.error(error);
      this.snackbarService.open('Une erreur est survenue lors de la création des demandes de documents.', 'error');
      return { success: false, magicLinkPath: null };
    }
  }

  private async retryDerTransmission(input: CreateDocumentRequestsInput): Promise<boolean> {
    try {
      const result = await this.myStelliumService.createDocumentRequests({ ...input, notifyInvestor: false });
      return await this.transmitDer(result?.derUploadUrl);
    } catch (error) {
      console.error(error);
      this.snackbarService.open(this.derNotTransmittedMessage, 'warning');
      return false;
    }
  }

  private async transmitDer(uploadUrl?: string | null): Promise<boolean> {
    const derDocumentUrl = this.documentRequestPanelService.validatedDer()?.url;
    if (!uploadUrl || !derDocumentUrl) {
      return true;
    }
    try {
      await this.myStelliumService.uploadDerDocument(uploadUrl, derDocumentUrl);
      return true;
    } catch (error) {
      console.error(error);
      this.snackbarService.open(this.derNotTransmittedMessage, 'warning');
      return false;
    }
  }

  private getDocumentRequests(): CreateDocumentRequestsInput['documentRequests'] {
    const typesByCode = new Map(this.documentTypes().map(type => [type.gedTypeCode, type]));
    const categoryLabelsByCode = new Map(this.categoryOptions().map(category => [category.value, category.label]));
    return this.selectedRequests().map(({ item, entry }) => {
      const documentTypes = item.documentTypeCodes.map(code => typesByCode.get(code)).filter((type): type is DocumentType => !!type);
      return {
        documentCategoryName: categoryLabelsByCode.get(documentTypes[0].documentCategory.gedCategoryCode) ?? '',
        documentTypeName: entry.label.trim(),
        documentRequestTypes: documentTypes.map(type => ({
          documentTypeCode: type.gedTypeCode,
          documentTypeName: type.label,
          expectedDocumentCount: type.maxExpectedDocument ?? 1,
          hasRectoVerso: type.hasRectoVerso ?? false,
        })),
      };
    });
  }

  private rebuildForm(): void {
    const documentTypes = this.documentTypes();
    if (!documentTypes.length) return;

    const previousEntries = new Map(this.model().items.map(item => [item.key, item.entries]));
    const existingCodes = new Set(this.getExistingRequests().flatMap(request => this.getRequestTypeCodes(request)));
    const groups = new Map<string, DocumentType[]>();

    for (const type of documentTypes.filter(type => type.availableForRequest)) {
      const key = type.groupName?.trim() ? `group:${type.groupName.trim()}` : `type:${type.gedTypeCode}`;
      groups.set(key, [...(groups.get(key) ?? []), type]);
    }

    const items = [...groups.entries()]
      .map(([key, types]): DocumentRequestFormItem => {
        const alreadyRequested = types.some(type => existingCodes.has(type.gedTypeCode));
        const entries = alreadyRequested ? [] : (previousEntries.get(key) ?? []);
        return {
          key,
          label: types[0].groupName?.trim() || types[0].label,
          categoryCode: types[0].documentCategory.gedCategoryCode,
          documentTypeCodes: types.map(type => type.gedTypeCode),
          selected: entries.length > 0,
          alreadyRequested,
          entries,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));

    this.model.set({ items });
  }

  private resetForm(): void {
    this.model.update(model => ({ items: model.items.map(item => this.withEntries(item, [])) }));
    this.search.set('');
    this.selectedCategoryCode.set(null);
    this.notifyByEmailChoice.set(true);
    this.recipientsExpanded.set(true);
  }

  private getExistingRequests(): DocumentRequestOutput[] {
    // En demande groupée, les demandes existantes de l'investisseur courant ne concernent pas les destinataires
    return this.isBulk() ? [] : (this.myStelliumService.documentRequestData()?.documentRequests ?? []);
  }

  // Codes demandés et codes des fichiers déposés (un dépôt libre ne porte que ces derniers)
  private getRequestTypeCodes(request: DocumentRequestOutput): string[] {
    return request.documentUploads
      .map(upload => upload.documentTypeCode)
      .filter((code): code is string => !!code)
      .concat(request.requestedTypeCodes);
  }
}
